import React, { useEffect } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { Redirect, useParams, useLocation } from "react-router-dom";
import { Lab, Logo, Pill } from "@/components/ms";
import Cookies from "js-cookie";
import { trackEvent } from "@/analytics";
import { AUTH_EVENTS } from "@/analytics/events";
import { AttributionTracker } from "@/utils/attribution";
import { useScreenTracking } from "@/hooks/useScreenTracking";
export const LoginButton = ({ redirectUrl }: { redirectUrl?: string }) => {
  const { loginWithRedirect } = useAuth0();

  return (
    <Pill
      variant="ghost"
      onClick={() => {
        // Get attribution data for tracking
        const attribution = AttributionTracker.getAttribution();

        // Track login attempt started
        trackEvent(AUTH_EVENTS.LOGIN_ATTEMPT_STARTED, {
          auth_method: "auth0",
          entry_point: redirectUrl ? "app_redirect" : "landing_page",
          source: attribution?.source,
          campaign: attribution?.campaign,
        });

        // TODO: Remove legacy tracking after migration to new event schema is complete
        trackEvent("login redirect via login button", {
          action: "login redirect",
          category: "login",
          label: "login redirect",
          value: 1,
          source: attribution?.source,
          campaign: attribution?.campaign
        });
        loginWithRedirect({
          appState: redirectUrl ? { targetUrl: redirectUrl } : undefined
        });
      }}
    >
      Log in
    </Pill>
  );
};
export const SignupButton = ({ redirectUrl }: { redirectUrl?: string }) => {
  const { loginWithRedirect } = useAuth0();

  return (
    <Pill
      onClick={() => {
        // Track login attempt started
        trackEvent(AUTH_EVENTS.LOGIN_ATTEMPT_STARTED, {
          auth_method: "auth0",
          entry_point: redirectUrl ? "app_redirect" : "landing_page",
        });

        // TODO: Remove legacy tracking after migration to new event schema is complete
        trackEvent("login redirect via signup button", {
          action: "login redirect",
          category: "login",
          label: "login redirect",
          value: 1,
        });

        loginWithRedirect({
          appState: redirectUrl ? { targetUrl: redirectUrl } : undefined
        });
      }}
    >
      Sign up
    </Pill>
  );
};

/** checks /:plan params in the url and sets a cookie and timestamp */
const checkPlanParam = (params) => {
  if (params?.plan) {
    const { plan } = params;
    console.log("[checkUpgrade] plan selected: ", plan);
    // set cookie
    Cookies.set("entrypoint_selected_plan", plan);
    // unix time
    const timestamp = Math.floor(Date.now() / 1000);
    Cookies.set("entrypoint_selected_plan_timestamp", timestamp);
    // now when we go to the homepage, we can read the cookie and know what plan they selected
    // write a cookie for the last time you checked for an upgrade and then check that cookie on the homepage
    // i.e. every 24 hours, check if they have upgraded
  } else {
    console.log("[checkUpgrade] no plan selected");
  }
  const selectedPlan = Cookies.get("entrypoint_selected_plan");
  if (selectedPlan) {
    console.log("[checkUpgrade] reading plan from cookie", selectedPlan);
  }
};

export const Login = () => {
  const { user, isAuthenticated, isLoading, getAccessTokenSilently, error } =
    useAuth0();

  // read the params from the url
  // if there is a plan param, then we want to redirect to the signup page with the plan param
  const params = useParams();
  const location = useLocation();

  // Extract redirect URL from query params
  const searchParams = new URLSearchParams(location.search);
  const redirectUrl = searchParams.get('redirect');

  // Track login screen viewed
  useScreenTracking({
    eventName: AUTH_EVENTS.LOGIN_SCREEN_VIEWED,
    screenName: "login",
    properties: {
      auth_method: "auth0",
      entry_point: redirectUrl ? "app_redirect" : "landing_page",
    },
  });

  // Track login errors
  useEffect(() => {
    if (error) {
      trackEvent(AUTH_EVENTS.LOGIN_FAILED, {
        auth_method: "auth0",
        entry_point: redirectUrl ? "app_redirect" : "landing_page",
        error_code: error.error || "unknown",
        error_message: error.message || "Authentication failed",
      });
    }
  }, [error, redirectUrl]);

  // checkPlanParam(params);

  if (isLoading) {
    return (
      <CentredState lab="Signing in" title="One moment" body="Checking your session." />
    );
  }

  return isAuthenticated ? (
    <Redirect to={redirectUrl || "/"} />
  ) : (
    <LoginWelcome redirectUrl={redirectUrl} />
  );
};

/** The document set's centred state: logo mark, Lab, 27px title, one sentence, one action. */
export const CentredState = ({
  lab,
  title,
  body,
  children,
}: {
  lab: string;
  title: string;
  body: React.ReactNode;
  children?: React.ReactNode;
}) => (
  <div className="flex min-h-screen flex-1 flex-col justify-center bg-ms-bg px-[var(--pad)] text-ms-ink">
    <div className="mx-auto flex w-full max-w-2xl flex-col items-start gap-4">
      <Logo width={30} />
      <div>
        <Lab size={9.5}>{lab}</Lab>
        <h1 className="mt-2.5 text-[27px] font-semibold leading-[1.15] tracking-[-0.032em]">
          {title}
        </h1>
        <p className="mt-2.5 max-w-[31em] text-[14px] leading-[1.5] text-ms-mute">{body}</p>
      </div>
      {children}
    </div>
  </div>
);

export const LoginWelcome = ({ redirectUrl }: { redirectUrl?: string | null }) => {
  return (
    <CentredState
      lab="Welcome"
      title="Welcome to Moonscape"
      body="Ready to tackle those crypto taxes? We're here to help."
    >
      <div className="flex flex-wrap gap-3">
        <SignupButton redirectUrl={redirectUrl || undefined} />
        <LoginButton redirectUrl={redirectUrl || undefined} />
      </div>
    </CentredState>
  );
};
